'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { useEffect, useRef, useState } from 'react';
import { ButtonIcon } from '../../ui/button-icon';

export type CardCartProps = {
  product: Product;
  sellable: boolean;
  showIcon: boolean;
  className?: string;
};
type Runtime = typeof import('./cart-button-runtime').CardCartRuntime;
let runtimePromise: Promise<Runtime> | undefined;

export function loadCardCartRuntime() {
  return (runtimePromise ??= import('./cart-button-runtime')
    .then(module => module.CardCartRuntime)
    .catch(error => {
      runtimePromise = undefined;
      throw error;
    }));
}

/** Only code is warmed on hover/focus; providers and queries mount after an actual click. */
export function DeferredCardCartButton(props: CardCartProps) {
  const { t } = useTranslations();
  const initialized = useCustomerStore(state => state.isInitialized);
  const [Runtime, setRuntime] = useState<Runtime>();
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const host = useRef<HTMLSpanElement>(null);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const pendingClick = useRef(false);
  const restoreFocus = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  function activate(click = false) {
    // Do not replace the pointer-down/focused element before the browser delivers its click.
    if (!click) {
      void loadCardCartRuntime().catch(() => {});
      return;
    }
    pendingClick.current = true;
    restoreFocus.current ||= !!host.current?.contains(document.activeElement);
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    setFailed(false);
    loadCardCartRuntime()
      .then(component => {
        if (mounted.current) setRuntime(() => component);
      })
      .catch(() => {
        pendingClick.current = false;
        if (mounted.current) setFailed(true);
      })
      .finally(() => {
        inFlight.current = false;
        if (mounted.current) setLoading(false);
      });
  }

  useEffect(() => {
    if (!Runtime) return;
    const button = host.current?.querySelector<HTMLButtonElement>('button');
    // Consume before dispatch so effect retries cannot add twice.
    const click = pendingClick.current;
    pendingClick.current = false;
    if (restoreFocus.current) button?.focus({ preventScroll: true });
    if (click && button && !button.disabled) button.click();
  }, [Runtime]);

  const supplier = [...(props.product.suppliers ?? [])].sort((a, b) => a.position - b.position)[0];
  const hasUnresolvedVariants = !!props.product.has_variants || !!props.product.variants?.length;
  if (
    !supplier?.is_affiliate &&
    !hasUnresolvedVariants &&
    getProductPriceDisplay(props.product).priceOnRequest
  )
    return null;
  if (supplier?.is_affiliate)
    return (
      <Button asChild className={props.className}>
        <a href={supplier.url ?? '#'} aria-label="Kúpiť">
          {props.showIcon && <ButtonIcon alt="" url="/images/icons/cart-add-white.svg" />}
          <span className="flex-1 text-center">Kúpiť</span>
        </a>
      </Button>
    );
  return (
    <span ref={host} className="contents">
      {Runtime ? (
        <Runtime key={`${props.product.id}-${props.product.slug}`} {...props} />
      ) : (
        <Button
          type="button"
          className={`${props.className ?? ''} max-h-12 min-h-12 min-w-12 sm:px-5`}
          disabled={!initialized || props.product.status === 'ended' || !props.sellable || loading}
          aria-label="Kúpiť"
          aria-busy={loading}
          onPointerEnter={() => activate()}
          onFocus={() => activate()}
          onPointerDown={() => activate()}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            activate(true);
          }}
        >
          {props.showIcon && <ButtonIcon alt="" url="/images/icons/cart-add-white.svg" />}
          <span className="hidden sm:inline">Kúpiť</span>
        </Button>
      )}
      {failed && (
        <span role="alert" className="sr-only">
          {t('text657')}
        </span>
      )}
    </span>
  );
}
